import { investorApi } from '@core/api/httpClient'
import { PORTFOLIO_ENDPOINTS } from './portfolioEndpointPaths'
import { parseInvestmentDetail } from './parsers/investmentDetailParser'
import {
  parsePortfolioAllocation,
  parsePortfolioGraphRental,
  parsePortfolioGraphValue,
  parsePortfolioHoldingsSummary,
  parsePortfolioOverview,
  parseRentalIncomeSummary,
} from './parsers/portfolioParser'
import type { InvestmentDetail } from '../types/investmentDetailTypes'
import type {
  PortfolioAllocation,
  PortfolioGraphRental,
  PortfolioGraphValue,
  PortfolioHoldingsSummary,
  PortfolioOverview,
  RentalIncomeSummary,
} from '../types/portfolioTypes'

export async function getRentalIncomeSummary(): Promise<RentalIncomeSummary> {
  const { data } = await investorApi.get<unknown>(PORTFOLIO_ENDPOINTS.RENTAL_INCOME_SUMMARY)
  return parseRentalIncomeSummary(data)
}

export async function getPortfolioOverview(): Promise<PortfolioOverview> {
  const { data } = await investorApi.get<unknown>(PORTFOLIO_ENDPOINTS.OVERVIEW)
  return parsePortfolioOverview(data)
}

/** `fiscalYear` ("2025-26", April–March); omitted = the current fiscal year. */
const graphParams = (type: 'value' | 'rental_income', fiscalYear?: string) =>
  fiscalYear ? { type, fiscalYear } : { type }

export async function getPortfolioGraphValue(fiscalYear?: string): Promise<PortfolioGraphValue> {
  const { data } = await investorApi.get<unknown>(PORTFOLIO_ENDPOINTS.GRAPH, {
    params: graphParams('value', fiscalYear),
  })
  return parsePortfolioGraphValue(data)
}

export async function getPortfolioGraphRental(fiscalYear?: string): Promise<PortfolioGraphRental> {
  const { data } = await investorApi.get<unknown>(PORTFOLIO_ENDPOINTS.GRAPH, {
    params: graphParams('rental_income', fiscalYear),
  })
  return parsePortfolioGraphRental(data)
}

export async function getPortfolioHoldingsSummary(): Promise<PortfolioHoldingsSummary> {
  const { data } = await investorApi.get<unknown>(PORTFOLIO_ENDPOINTS.HOLDINGS_SUMMARY)
  return parsePortfolioHoldingsSummary(data)
}

export async function getPortfolioHoldings(page = 1, limit = 10): Promise<unknown> {
  const { data } = await investorApi.get<unknown>(
    `${PORTFOLIO_ENDPOINTS.HOLDINGS}?page=${page}&limit=${limit}`,
  )
  return data
}

export async function getPortfolioAllocation(): Promise<PortfolioAllocation> {
  const { data } = await investorApi.get<unknown>(PORTFOLIO_ENDPOINTS.ALLOCATION)
  return parsePortfolioAllocation(data)
}

export async function getInvestmentDetail(propertyId: string): Promise<InvestmentDetail> {
  const url = PORTFOLIO_ENDPOINTS.HOLDING_DETAIL.replace('{propertyId}', propertyId)
  const { data } = await investorApi.get<unknown>(url)
  return parseInvestmentDetail(data)
}
